import { Avatar, Box, IconButton, Stack, Typography } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import React from 'react';
import parse from 'html-react-parser';
import DOMPurify from 'dompurify';

export const ContentParser = ({ value }: { value: string }) => {
  return (
    <Box
      sx={{
        a: {
          color: 'inherit',
        },
      }}
    >
      {parse(DOMPurify.sanitize(value))}
    </Box>
  );
};

interface IMessageFromProps {
  body: any;
  time: string;
  avatar: any;
  authorName: string;
  tags?: string[];
  conversationId: string;
  conversationPartIdRef: string;
  tagCurrent?: string;
  /** Tags on this message an admin may take off; each gets a remove control. */
  removableTags?: string[];
  onRemoveTag?: (tag: string) => void;
}

export const MessageFrom = ({
  body,
  time,
  avatar,
  authorName,
  tags,
  conversationId,
  conversationPartIdRef,
  tagCurrent,
  removableTags,
  onRemoveTag,
}: IMessageFromProps) => {
  const refAbsolute = React.useRef<any>();
  const [tagRowWidth, setTagRowWidth] = React.useState<number>();

  // The tag row is absolutely positioned above the bubble and can be wider
  // than a short message, so the bubble has to make room for it. As a floor,
  // not a fixed width: freezing the mount-time scrollWidth left bubbles at
  // their original size once the thread moved into the narrower inbox pane.
  React.useEffect(() => {
    setTagRowWidth(tags?.length ? refAbsolute.current?.scrollWidth : undefined);
  }, [tags, removableTags?.join('|')]);

  return (
    <Stack
      direction={'row'}
      spacing={1}
      alignItems={'end'}
      padding={'8px'}
      // marginTop={conversationId === conversationPartIdRef ? '16px' : '0px'}
    >
      {avatar ? (
        <img
          src={avatar}
          className="h-[23px] w-[23px]"
          style={{
            borderRadius: '50%',
          }}
        />
      ) : (
        <Avatar
          sx={{
            color: 'white',
            fontSize: '13px',
            backgroundColor: '#213B54',
            fontWeight: 600,
          }}
          className="!h-[23px] !w-[23px]"
        >
          {authorName?.trim()?.charAt(0) || ''}
        </Avatar>
      )}
      <Stack
        sx={{
          backgroundColor: '#FAF7F4',
          padding: '12px',
          minWidth: tagRowWidth,
          textAlign: 'right',
          maxWidth: '80%',
          borderRadius: '15px',
          boxShadow: '0px 2px 8px 0px #00000026',
          position: 'relative',
          zIndex: 1,
        }}
        spacing={1}
      >
        <Stack
          sx={{
            width: 'max-content',
            position: 'absolute',
            top: '-16px',
            left: '0px',
            zIndex: 2,
            minWidth: '160px',
          }}
          direction={'row'}
          ref={refAbsolute}
        >
          {tags &&
            tags.length > 0 &&
            tags.map((tag) => {
              const removable = !!onRemoveTag && !!removableTags?.includes(tag);
              return (
                <Stack
                  key={tag}
                  direction="row"
                  alignItems="center"
                  sx={{
                    color: '#356766',
                    backgroundColor:
                      conversationId === conversationPartIdRef &&
                      tagCurrent === tag
                        ? 'rgba(239, 207, 81, 0.44)'
                        : '#EAEFEF',
                    border: '1px solid #356766',
                    borderRadius: '20px',
                    paddingLeft: '8px',
                    paddingRight: removable ? '2px' : '8px',
                    height: '24px',
                    marginRight: '4px',
                    boxShadow: '2px 2px rgba(0, 0, 0, 0.25)',
                  }}
                >
                  <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                    {tag}
                  </Typography>
                  {removable && (
                    <IconButton
                      aria-label={`Remove the ${tag} tag from this message`}
                      onClick={() => onRemoveTag?.(tag)}
                      sx={{
                        marginLeft: '2px',
                        padding: '2px',
                        color: '#356766',
                        '&:hover, &.Mui-focusVisible': {
                          color: '#FFFFFF',
                          backgroundColor: '#356766',
                        },
                        '&.Mui-focusVisible': {
                          outline: '2px solid #213B54',
                          outlineOffset: '1px',
                        },
                        // A bigger hit area than the 18px it draws.
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          inset: '-3px',
                          borderRadius: '50%',
                        },
                      }}
                    >
                      <CloseRoundedIcon sx={{ fontSize: '14px' }} />
                    </IconButton>
                  )}
                </Stack>
              );
            })}
        </Stack>
        <Typography
          sx={{
            lineHeight: '18px',
            textAlign: 'left',
            fontSize: '14px',
            fontWeight: 400,
            color: '#000000',
            wordWrap: 'break-word',
          }}
        >
          <ContentParser value={body || ''} />
        </Typography>
        <Typography
          sx={{
            textAlign: 'start',
            fontSize: '12px',
            fontWeight: 500,
            color: '#7D818C',
            width: '100%',
          }}
        >
          {time}
        </Typography>
      </Stack>
    </Stack>
  );
};
